import type { Property } from 'csstype';
import React, { memo, useCallback, useMemo } from 'react';

import View from '@/components/atomic/atoms/layout//view';
import GradientOverlay from '@/components/atomic/atoms/layout/image/gradient-overlay.styled';
import Img from '@/components/atomic/atoms/layout/image/img';
import Picture from '@/components/atomic/atoms/layout/image/picture';
import type { ImageProps } from '@/components/atomic/atoms/layout/image/typing';
import type { ViewProps } from '@/components/atomic/atoms/layout/view/typings';
import withExposer from '@/hocs/with-exposer';
import { useIsInEditor } from '@/hooks/use-is-in-editor';
import useStore from '@/state';
import getDynamicRenditionsConfig from '@/state/selectors/navigation/get-dynamic-renditions-config';
import { BasicComponentProps } from '@/typings/generic';
import normalisePath from '@/utils/path-helper';
import { typedObjectKeys } from '@multitenancy-spa/utils/esm/utils/typed-object-keys';

const ImageComponent: React.FC<
	ImageProps &
		ViewProps &
		BasicComponentProps & {
			$pictureHeight?: number | string;
			$pictureWidth?: number | string;
			$usePreviewWidth?: boolean;
			$usePreviewAspectRatio?: boolean;
			$flex?: Property.Flex;
		}
> = ({
	alt,
	sources,
	src,
	$height,
	$width,
	$exposerSuffix,
	$breakpoints,
	$padding,
	$pictureHeight,
	$pictureWidth,
	$position,
	$aspectRatio,
	$objectFit,
	darkTintEnabled,
	$usePreviewWidth,
	$usePreviewAspectRatio,
	$flex,
	$isThumbnail,
	useDynamicMedia = true,
}) => {
	const isInEditor = useIsInEditor();

	const isDynamicMediaImage = useMemo(
		() => useDynamicMedia && src?.includes('scene7.com'),
		[src, useDynamicMedia],
	);

	const dynamicRenditionsConfig = useStore(getDynamicRenditionsConfig);

	const normaliseSrc = useCallback(
		(path: string) => {
			let url = path;
			if (src && isDynamicMediaImage) {
				let uri: URL;
				try {
					uri = new URL(src);
					url = `${uri.origin}${uri.pathname}`;
				} catch {
					//
				}
			}
			return url;
		},
		[isDynamicMediaImage, src],
	);

	const dynamicMediaSrc = useMemo(() => {
		let url = src ? normaliseSrc(src) : undefined;

		if (
			src &&
			$isThumbnail &&
			dynamicRenditionsConfig?.defaultThumbnailParameters &&
			src.includes('scene7.com') &&
			useDynamicMedia
		) {
			url = `${url}?${dynamicRenditionsConfig.defaultThumbnailParameters}`;
		}

		return url;
	}, [
		normaliseSrc,
		src,
		$isThumbnail,
		dynamicRenditionsConfig?.defaultThumbnailParameters,
		useDynamicMedia,
	]);

	const width = $usePreviewWidth ? $width : $width;
	const aspectRatio = $usePreviewAspectRatio
		? $aspectRatio
		: isInEditor
			? '16 / 9'
			: $aspectRatio;

	const sourcesProps = useMemo<
		React.DetailedHTMLProps<
			React.SourceHTMLAttributes<HTMLSourceElement>,
			HTMLSourceElement
		>[]
	>(() => {
		if (!sources) return [];

		const sizes = typedObjectKeys(sources);

		return sizes.map((size) => ({
			media: `(max-width: ${size}px)`,
			srcSet: sources[size],
		}));
	}, [sources]);

	const rensitionsProps = useMemo<
		React.DetailedHTMLProps<
			React.SourceHTMLAttributes<HTMLSourceElement>,
			HTMLSourceElement
		>[]
	>(() => {
		if (
			!isDynamicMediaImage ||
			!dynamicRenditionsConfig?.defaultBreakpoints ||
			$isThumbnail
		) {
			return [];
		}

		return dynamicRenditionsConfig.defaultBreakpoints.map((size) => ({
			media: `(max-width: ${size}px)`,
			srcSet: normalisePath(
				src ? `${normaliseSrc(src)}?wid=${size}` : undefined,
			),
		}));
	}, [
		dynamicRenditionsConfig?.defaultBreakpoints,
		isDynamicMediaImage,
		normaliseSrc,
		src,
		$isThumbnail,
	]);

	const renditions = useMemo(() => {
		if (rensitionsProps.length > 0) return rensitionsProps;
		return sourcesProps;
	}, [sourcesProps, rensitionsProps]);

	const publicPathSrc = useMemo(() => {
		if (
			dynamicMediaSrc?.startsWith('/assets') &&
			process.env.NODE_ENV !== 'development'
		) {
			return normalisePath(
				`${globalThis.viteExternalPublicPath}${dynamicMediaSrc}`,
			);
		}
		return normalisePath(dynamicMediaSrc);
	}, [dynamicMediaSrc]);

	const ImageComponent = useMemo(
		() => (
			<Picture
				$exposerSuffix={$exposerSuffix}
				$width={$pictureWidth}
				$height={$pictureHeight}
				$position={$position}
				$aspectRatio={aspectRatio}
				$flex={$flex}
			>
				{renditions.map((rendition) => (
					<source key={rendition.media} {...rendition} />
				))}
				<Img
					$exposerSuffix={$exposerSuffix}
					src={publicPathSrc}
					alt={alt}
					$width={width}
					$height={$height}
					$breakpoints={$breakpoints}
					$padding={$padding}
					$objectFit={$objectFit}
				/>
			</Picture>
		),
		[
			$exposerSuffix,
			$pictureWidth,
			$pictureHeight,
			$position,
			aspectRatio,
			$flex,
			renditions,
			publicPathSrc,
			alt,
			width,
			$height,
			$breakpoints,
			$padding,
			$objectFit,
		],
	);

	if (darkTintEnabled) {
		return (
			<View $position='relative'>
				<GradientOverlay />
				{ImageComponent}
			</View>
		);
	}

	return ImageComponent;
};

const Image = memo(withExposer(ImageComponent, 'image'));

export default Image;
